import {
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import React, { useEffect, useState } from "react";
import { useToggle } from "../hooks/useToggle";

type Props = {
  date?: string | null;
  title: string;
  onSave: (date: string) => Promise<void>;
};

export function EditDateButton({ date, title, onSave }: Props) {
  const [loading, startLoading, endLoading] = useToggle(false);
  const [open, openDialog, closeDialog] = useToggle(false);
  const defaultDate = date
    ? [
        new Date(date).getFullYear(),
        (new Date(date).getMonth() + 1).toString().padStart(2, "0"),
        new Date(date).getDate().toString().padStart(2, "0"),
      ].join("-")
    : undefined;
  const [selectedDate, setSelectedDate] = useState<string | undefined>(defaultDate);
  const notChanged = defaultDate === selectedDate;

  const onConfirm = async () => {
    if (!selectedDate) return;

    try {
      startLoading();
      await onSave(new Date(selectedDate).toISOString());
      closeDialog();
    } catch (e) {
      console.error(e);
    } finally {
      endLoading();
    }
  };

  useEffect(() => {
    if (!open) {
      setSelectedDate(defaultDate);
    }
  }, [open, defaultDate]);

  return (
    <>
      <IconButton aria-label="edit" color="primary" onClick={openDialog} sx={{ fontSize: "14px" }}>
        <EditIcon fontSize="inherit" />
      </IconButton>
      <Dialog open={open} onClose={closeDialog} fullScreen={true}>
        <DialogTitle>
          <Typography variant="h6" fontSize="18px">
            {title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            label="Date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            fullWidth={true}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ marginTop: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDialog}>
            Cancel
          </Button>
          <Button variant="contained" disabled={loading || notChanged || !selectedDate} onClick={onConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
